import {
  Box, Stack, SimpleGrid, Text, Badge, Group, Table, Anchor, Button, ThemeIcon,
} from '@mantine/core'
import {
  IconGitBranch,
  IconGitPullRequest,
  IconRocket,
  IconClock,
  IconExternalLink,
  IconRefresh,
  IconBrandGitlab,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { SmallBox, DataCard, Timeline, EmptyState, Callout } from '@/components/ui'
import { DeployFrequencyChart, getISOWeek } from '@/components/charts/DeployFrequencyChart'
import {
  gitlabApi,
  type Pipeline,
  type MergeRequest,
  type ActivityEvent,
  type MetricsSummary,
  type GitLabConfig,
  type PipelinesResponse,
  type MergeRequestsResponse,
  type ActivityResponse,
} from '@/api/gitlab'

const PIPELINE_STATUS_COLOR: Record<string, string> = {
  success: 'green',
  failed: 'red',
  running: 'blue',
  pending: 'yellow',
  canceled: 'gray',
}

function timeAgo(dateStr: string): { text: string; color: string } {
  const diff = Date.now() - new Date(dateStr).getTime()
  const hours = Math.floor(diff / 3_600_000)
  const days = Math.floor(hours / 24)
  if (days > 7) return { text: `${days}d`, color: 'red' }
  if (days > 3) return { text: `${days}d`, color: 'orange' }
  if (days >= 1) return { text: `${days}d`, color: 'yellow' }
  if (hours >= 1) return { text: `${hours}h`, color: 'green' }
  return { text: '<1h', color: 'green' }
}

function GitLabNotConfigured() {
  return (
    <EmptyState
      icon={IconBrandGitlab}
      accent="warning"
      title="GitLab não configurado"
      description="Configure a integração GitLab para visualizar pipelines, merge requests, deploys e métricas DORA do tenant."
      primaryAction={{
        label: 'Configurar GitLab',
        href: '/admin/tenants',
        permission: { resource: 'usuarios', action: 'manage' },
      }}
    />
  )
}

export function ApplicationTab() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const qc = useQueryClient()

  const config = useQuery<GitLabConfig>({
    queryKey: ['gitlab-config', tenantId],
    queryFn: () => gitlabApi.getConfig(tenantId!),
    enabled: !!tenantId,
    staleTime: 300_000,
    retry: false,
  })

  const isConfigured = config.isSuccess && config.data?.sync_enabled

  const pipelines = useQuery<PipelinesResponse>({
    queryKey: ['gitlab-pipelines', tenantId],
    queryFn: () => gitlabApi.getPipelines(tenantId!),
    enabled: !!tenantId && isConfigured,
    staleTime: 60_000,
  })

  const mergeRequests = useQuery<MergeRequestsResponse>({
    queryKey: ['gitlab-mrs', tenantId],
    queryFn: () => gitlabApi.getMergeRequests(tenantId!),
    enabled: !!tenantId && isConfigured,
    staleTime: 60_000,
  })

  const activity = useQuery<ActivityResponse>({
    queryKey: ['gitlab-activity', tenantId],
    queryFn: () => gitlabApi.getActivity(tenantId!),
    enabled: !!tenantId && isConfigured,
    staleTime: 60_000,
  })

  const metrics = useQuery<MetricsSummary>({
    queryKey: ['gitlab-metrics', tenantId],
    queryFn: () => gitlabApi.getMetrics(tenantId!),
    enabled: !!tenantId && isConfigured,
    staleTime: 60_000,
  })

  const syncMutation = useMutation({
    mutationFn: () => gitlabApi.sync(tenantId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gitlab-pipelines', tenantId] })
      qc.invalidateQueries({ queryKey: ['gitlab-mrs', tenantId] })
      qc.invalidateQueries({ queryKey: ['gitlab-activity', tenantId] })
      qc.invalidateQueries({ queryKey: ['gitlab-metrics', tenantId] })
    },
  })

  if (config.isLoading) return null
  if (!isConfigured) return <GitLabNotConfigured />

  const pipelineList = pipelines.data?.pipelines ?? []
  const mrList = mergeRequests.data?.merge_requests ?? []
  const openMRs = mrList.filter(mr => mr.state === 'opened')
  const m = metrics.data
  const branch = pipelines.data?.branch ?? config.data?.default_branch ?? 'main'
  const lastPipeline = pipelineList[0]

  // `degraded` = a api avisou que a leitura no GitLab estourou o deadline. As
  // quatro consultas degradam independentemente, entao cada bloco olha a SUA
  // flag; o aviso do topo aparece se qualquer uma degradou. TASK-10996.
  const pipelinesDegradado = pipelines.data?.degraded === true
  const mrsDegradado = mergeRequests.data?.degraded === true
  const activityDegradado = activity.data?.degraded === true
  const metricsDegradado = metrics.data?.degraded === true
  const algoDegradado = pipelinesDegradado || mrsDegradado || activityDegradado || metricsDegradado

  const leadTimeText = metricsDegradado
    ? '—'
    : m?.lead_time_hours != null && m.lead_time_hours > 0
      ? (m.lead_time_hours < 24 ? `${Math.round(m.lead_time_hours)}h` : `${Math.round(m.lead_time_hours / 24)}d`)
      : '—'

  // Build weekly deploy data for chart
  const deployWeeks = buildWeeklyDeploys(pipelineList)

  return (
    <Stack gap="xl">
      {algoDegradado && (
        <Callout variant="warning" title="Leitura do GitLab incompleta">
          A consulta ao GitLab não respondeu dentro do prazo, então os números abaixo estão
          <strong> incompletos ou zerados por falta de leitura</strong> — não são medição do time.
          Use o botão <strong>Sync</strong> para tentar de novo.
        </Callout>
      )}

      {/* Header */}
      <Group justify="space-between">
        <Group gap="sm">
          <IconBrandGitlab size={24} />
          <Text fw={600} size="lg">{config.data?.gitlab_group_url?.split('/').pop() ?? 'GitLab'}</Text>
          <Badge variant="light" size="sm">{branch}</Badge>
          {(config.data?.gitlab_project_ids?.length ?? 0) > 0 && (
            <Badge variant="light" size="sm" color="grape">
              {config.data?.gitlab_project_ids?.length} {config.data?.gitlab_project_ids?.length === 1 ? 'projeto' : 'projetos'}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={() => syncMutation.mutate()}
            loading={syncMutation.isPending}
          >
            Sync
          </Button>
          {config.data?.gitlab_group_url && (
            <Anchor href={config.data.gitlab_group_url} target="_blank" size="sm">
              <Group gap={4}>
                <Text size="xs">Abrir GitLab</Text>
                <IconExternalLink size={12} />
              </Group>
            </Anchor>
          )}
        </Group>
      </Group>

      {/* Row 1: Metrics */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <SmallBox
          value={lastPipeline?.status ?? '—'}
          label="Último Pipeline"
          icon={<IconGitBranch size={40} />}
          color={lastPipeline ? (lastPipeline.status === 'success' ? 'success' : lastPipeline.status === 'failed' ? 'danger' : 'info') : 'info'}
          loading={pipelines.isLoading}
        />
        <SmallBox
          value={mrsDegradado ? '—' : openMRs.length}
          label="MRs Abertas"
          icon={<IconGitPullRequest size={40} />}
          color="info"
          loading={mergeRequests.isLoading}
        />
        <SmallBox
          value={
            // Degradado devolve `deploys_30d: 0`, e `??` nao cai no fallback por
            // 0 nao ser nullish — sem este ramo o zero da falha vira "0 deploys".
            metricsDegradado
              ? (pipelinesDegradado ? '—' : pipelineList.length)
              : (m?.deploys_30d ?? pipelineList.length)
          }
          label="Deploys 30d"
          icon={<IconRocket size={40} />}
          color="primary"
          loading={metrics.isLoading}
        />
        <SmallBox
          value={leadTimeText}
          label="Lead Time"
          icon={<IconClock size={40} />}
          color="success"
          loading={metrics.isLoading}
        />
      </SimpleGrid>

      {/* Row 2: Pipelines Table */}
      <DataCard flush title="Pipelines Recentes" accent="primary" refreshable onRefresh={() => pipelines.refetch()} loading={pipelines.isLoading}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Projeto</Table.Th>
              <Table.Th>Commit</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Duração</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pipelineList.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text ta="center" c="dimmed" py="xl">
                    {pipelinesDegradado
                      ? 'Não foi possível ler os pipelines no GitLab (tempo excedido)'
                      : 'Nenhum pipeline encontrado'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              pipelineList.slice(0, 10).map((p: Pipeline) => (
                <Table.Tr key={`${p.project_id ?? 0}-${p.id}`}>
                  <Table.Td>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {p.project_path?.split('/').slice(-2).join('/') ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Text size="xs" ff="monospace" c="dimmed">
                        {p.sha?.slice(0, 8) ?? `#${p.id}`}
                      </Text>
                      <Text size="sm" lineClamp={1}>
                        {p.commit_message ?? p.ref}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={PIPELINE_STATUS_COLOR[p.status] ?? 'gray'}>
                      {p.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {p.duration ? `${Math.round(p.duration / 60)}min` : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {p.web_url && (
                      <Anchor href={p.web_url} target="_blank" size="xs">
                        <IconExternalLink size={14} />
                      </Anchor>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </DataCard>

      {/* Row 3: MRs + Deploy Frequency */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <DataCard flush title="Merge Requests Abertas" accent="info" refreshable onRefresh={() => mergeRequests.refetch()} loading={mergeRequests.isLoading}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Projeto</Table.Th>
                <Table.Th>Título</Table.Th>
                <Table.Th>Autor</Table.Th>
                <Table.Th>Aberta</Table.Th>
                <Table.Th>Labels</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {openMRs.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text ta="center" c="dimmed" py="md">
                      {mrsDegradado
                        ? 'Não foi possível ler as merge requests no GitLab (tempo excedido)'
                        : 'Nenhuma MR aberta'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                openMRs.slice(0, 8).map((mr: MergeRequest) => {
                  const age = timeAgo(mr.created_at)
                  return (
                    <Table.Tr key={`${mr.project_id ?? 0}-${mr.iid}`}>
                      <Table.Td>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {mr.project_path?.split('/').slice(-2).join('/') ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Text size="sm" lineClamp={1}>{mr.title}</Text>
                          {mr.has_conflicts && (
                            <Badge size="xs" color="red" variant="filled">conflito</Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">{mr.source_branch} → {mr.target_branch}</Text>
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{mr.author?.name ?? '—'}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={age.color} variant="light">{age.text}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {(mr.labels ?? []).slice(0, 2).map((l: string) => (
                            <Badge key={l} size="xs" variant="light">{l}</Badge>
                          ))}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })
              )}
            </Table.Tbody>
          </Table>
        </DataCard>

        <DataCard flush title="Frequência de Deploy (30d)" accent="primary">
          <Box p="md">
            {/* O grafico sai dos pipelines; sem leitura ele desenharia semanas
                vazias, que na tela sao indistinguiveis de "ninguem deployou". */}
            {pipelinesDegradado ? (
              <Text ta="center" c="dimmed" py="xl">
                Sem leitura dos pipelines — o gráfico não é medição
              </Text>
            ) : (
              <DeployFrequencyChart data={deployWeeks} />
            )}
          </Box>
        </DataCard>
      </SimpleGrid>

      {/* Row 4: Activity Feed */}
      <DataCard flush title="Atividade Recente" accent="success" refreshable onRefresh={() => activity.refetch()} loading={activity.isLoading}>
        {(activity.data?.events ?? []).length === 0 ? (
          <Box p="xl">
            <Text ta="center" c="dimmed">
              {activityDegradado
                ? 'Não foi possível ler a atividade no GitLab (tempo excedido)'
                : 'Nenhuma atividade recente'}
            </Text>
          </Box>
        ) : (
          <Box p="md">
            <Timeline
              items={(activity.data?.events ?? []).slice(0, 10).map((e: ActivityEvent) => ({
                title: e.title || `${e.type}: ${e.status}`,
                time: e.timestamp ? new Date(e.timestamp).toLocaleString('pt-BR') : '',
                color: e.type === 'pipeline'
                  ? (e.status === 'success' ? 'success' as const : e.status === 'failed' ? 'warning' as const : 'primary' as const)
                  : (e.status === 'merged' ? 'info' as const : e.status === 'opened' ? 'warning' as const : 'primary' as const),
              }))}
            />
          </Box>
        )}
      </DataCard>
    </Stack>
  )
}

function buildWeeklyDeploys(pipelines: Pipeline[]): { week: string; success: number; failed: number }[] {
  const weeks: Record<string, { success: number; failed: number }> = {}
  for (const p of pipelines) {
    if (!p.created_at) continue
    const d = new Date(p.created_at)
    const { year, week } = getISOWeek(d)
    const key = `${year}-W${String(week).padStart(2, '0')}`
    if (!weeks[key]) weeks[key] = { success: 0, failed: 0 }
    if (p.status === 'success') weeks[key].success++
    else if (p.status === 'failed') weeks[key].failed++
  }
  return Object.entries(weeks)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-4)
    .map(([week, data]) => ({ week, ...data }))
}
